import React, { createContext, ReactNode, useContext, useState } from 'react'

interface IButton {
  label: string
  onClick?: () => void
}

interface IWarningPopup {
  onClose?: () => void
  title: string | ReactNode
  description: string | ReactNode
  imageSrc?: string
  buttons: {
    cancel?: IButton
    confirm: IButton
  }
}

interface IConfirmationPopup {
  title: string | ReactNode
  description?: string | ReactNode
  imageSrc?: string
  buttons: {
    cancel?: IButton
    confirm: IButton
  }
}

interface IErrorPopup {
  type: string
}

export interface IFullStoragePopup {
  isOpen?: boolean
  onClose?: () => void
}

interface IAlertPopupsData {
  warningPopup: IWarningPopup
  confirmationPopup: IConfirmationPopup
  errorPopup: IErrorPopup
  fullStoragePopup: IFullStoragePopup
}

interface IAlertPopupsContextType {
  alertPopupsData: IAlertPopupsData
  setAlertPopupsData: (data: Partial<IAlertPopupsData>) => void
  resetAlertPopupsData: () => void
}

const INITIAL_STATE: IAlertPopupsData = {
  warningPopup: {
    title: '',
    description: '',
    imageSrc: '',
    buttons: {
      cancel: {
        label: '',
        onClick: () => {}
      },
      confirm: {
        label: '',
        onClick: () => {}
      }
    }
  },
  confirmationPopup: {
    title: '',
    description: '',
    imageSrc: '',
    buttons: {
      cancel: {
        label: '',
        onClick: () => {}
      },
      confirm: {
        label: '',
        onClick: () => {}
      }
    }
  },
  errorPopup: {
    type: ''
  },
  fullStoragePopup: {
    isOpen: false
  }
}

const AlertPopupsContext = createContext<IAlertPopupsContextType | undefined>(
  undefined
)

export const AlertPopupsProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [data, setData] = useState<IAlertPopupsData>(INITIAL_STATE)

  const setAlertPopupsData = (data: Partial<IAlertPopupsData>) => {
    setData(prev => ({ ...prev, ...data }))
  }

  const resetAlertPopupsData = () => {
    setData(INITIAL_STATE)
  }

  return (
    <AlertPopupsContext.Provider
      value={{
        alertPopupsData: data,
        setAlertPopupsData,
        resetAlertPopupsData
      }}
    >
      {children}
    </AlertPopupsContext.Provider>
  )
}

export const useAlertPopupsContext = () => {
  const context = useContext(AlertPopupsContext)

  if (!context) {
    throw new Error(
      'useAlertPopupsContext must be used within a AlertPopupsProvider'
    )
  }

  return context
}
